/**
 * Taxonomy Lookup Helpers — Pure functions for querying the taxonomy.
 *
 * Split from taxonomyRegistry.ts in R23 (DDD Fase 3).
 * All functions operate on the lookup maps built in taxonomyRegistry.ts.
 */

import type { ModuleId } from '../types';
import type { FamilyDefinition } from './types';
import { TAXONOMY, FAMILY_BY_ID, FAMILY_BY_SUBSKILL, FAMILIES_BY_MODULE, FAMILIES_BY_MODULE_SKILL } from './taxonomyRegistry';

// ============================================================
// Basic Lookups
// ============================================================

/** Check if a familyId exists in the taxonomy. */
export function isValidFamilyId(id: string): boolean {
  return FAMILY_BY_ID.has(id);
}

/** Get the FamilyDefinition for a familyId. */
export function getFamilyDefinition(id: string): FamilyDefinition | undefined {
  return FAMILY_BY_ID.get(id);
}

/** Get the student-facing learning goal for a familyId (Feature 13). */
export function getLearningGoal(familyId: string): string {
  const family = FAMILY_BY_ID.get(familyId);
  if (!family) return '';
  return family.learningGoal || family.description;
}

/** Get all families for a module. */
export function getFamiliesForModule(moduleId: ModuleId): FamilyDefinition[] {
  return FAMILIES_BY_MODULE.get(moduleId) || [];
}

/** Get the tier for a familyId. */
export function getTierForFamily(familyId: string): number | undefined {
  return getFamilyDefinition(familyId)?.tier;
}

/** Check if a subSkill exists in the taxonomy. */
export function isValidSubSkill(subSkill: string): boolean {
  return FAMILY_BY_SUBSKILL.has(subSkill);
}

/** Get the FamilyDefinition for a subSkill. */
export function getFamilyBySubSkill(subSkill: string): FamilyDefinition | undefined {
  return FAMILY_BY_SUBSKILL.get(subSkill);
}

// ============================================================
// Consistency Validation
// ============================================================

/**
 * Validate that a familyId, module, and subSkill are consistent.
 * Returns an array of error strings (empty = valid).
 */
export function validateFamilyConsistency(familyId: string, module: ModuleId, subSkill: string): string[] {
  const errors: string[] = [];
  const def = getFamilyDefinition(familyId);
  if (!def) {
    errors.push(`Unknown familyId: "${familyId}"`);
    return errors;
  }
  if (def.module !== module) {
    errors.push(`Module mismatch for ${familyId}: taxonomy says "${def.module}", got "${module}"`);
  }
  if (def.subSkill !== subSkill) {
    errors.push(`SubSkill mismatch for ${familyId}: taxonomy says "${def.subSkill}", got "${subSkill}"`);
  }
  return errors;
}

// ============================================================
// Authoritative Lookups
// ============================================================

/**
 * Get the authoritative tier for a familyId.
 * Questions MUST use this tier — they cannot override it.
 */
export function getAuthoritativeTier(familyId: string): number | undefined {
  return getFamilyDefinition(familyId)?.tier;
}

/** Get the authoritative levelCode for a familyId. */
export function getAuthoritativeLevelCode(familyId: string): string | undefined {
  return getFamilyDefinition(familyId)?.levelCode;
}

// ============================================================
// Module-Scoped Skill Queries
// ============================================================

/** Get all skills for a module (unique list). */
export function getSkillsForModule(moduleId: ModuleId): string[] {
  const families = getFamiliesForModule(moduleId);
  return [...new Set(families.map(f => f.skill))];
}

/**
 * Create a module-scoped skill key.
 * Format: "ModuleId::skill"
 */
export function getModuleScopedSkillKey(moduleId: ModuleId, skill: string): string {
  assertModuleScopedSkillInput(skill);
  return `${moduleId}::${skill}`;
}
function assertModuleScopedSkillInput(skill: string): void {
  if (skill.includes('::')) {
    throw new Error(`Expected bare skill name without module scope, received "${skill}". Pass module and skill separately.`);
  }
}

/**
 * Get all families for a specific skill within a module.
 * This is the PREFERRED method for skill queries (module-scoped).
 */
export function getFamiliesForModuleSkill(moduleId: ModuleId, skill: string): FamilyDefinition[] {
  const key = getModuleScopedSkillKey(moduleId, skill);
  return FAMILIES_BY_MODULE_SKILL.get(key) || [];
}

/**
 * Get all unique module-scoped skills across all modules.
 * Returns keys in format "ModuleId::skill"
 */
export function getAllModuleScopedSkills(): string[] {
  return Array.from(FAMILIES_BY_MODULE_SKILL.keys());
}

/**
 * Check if a module-scoped skill exists.
 */
export function isValidModuleSkill(moduleId: ModuleId, skill: string): boolean {
  const key = getModuleScopedSkillKey(moduleId, skill);
  return FAMILIES_BY_MODULE_SKILL.has(key);
}

// ============================================================
// Statistics & Analysis
// ============================================================

/**
 * Get skill statistics for a module.
 */
export function getSkillStatsForModule(moduleId: ModuleId) {
  const families = getFamiliesForModule(moduleId);
  const skillGroups = new Map<string, number>();
  families.forEach(f => {
    skillGroups.set(f.skill, (skillGroups.get(f.skill) || 0) + 1);
  });
  return {
    module: moduleId,
    totalSkills: skillGroups.size,
    totalFamilies: families.length,
    skillBreakdown: Array.from(skillGroups.entries()).map(([skill, count]) => ({
      skill,
      moduleScopedKey: getModuleScopedSkillKey(moduleId, skill),
      familyCount: count
    })).sort((a, b) => b.familyCount - a.familyCount)
  };
}

/**
 * Validate skill consistency across the taxonomy.
 * Ensures that skill groupings are logical and complete.
 */
export function validateSkillConsistency(): string[] {
  const warnings: string[] = [];

  // Check for orphaned skills (skills with only 1 family might be miscategorized)
  FAMILIES_BY_MODULE_SKILL.forEach((families, moduleScopedKey) => {
    if (families.length === 1) {
      warnings.push(`Module-scoped skill "${moduleScopedKey}" has only 1 family (${families[0].familyId}). Consider if this should be grouped differently.`);
    }
  });

  // Check for skill naming consistency (should be lowercase with underscores)
  FAMILIES_BY_MODULE_SKILL.forEach((families, moduleScopedKey) => {
    const skill = moduleScopedKey.split('::')[1];
    if (skill !== skill.toLowerCase()) {
      warnings.push(`Skill "${skill}" in ${moduleScopedKey} should be lowercase`);
    }
    if (skill.includes('-') || skill.includes(' ')) {
      warnings.push(`Skill "${skill}" in ${moduleScopedKey} should use underscores, not hyphens or spaces`);
    }
  });

  // Check for skill name collisions across modules (informational, not an error)
  const skillNameCounts = new Map<string, Set<ModuleId>>();
  TAXONOMY.forEach(f => {
    if (!skillNameCounts.has(f.skill)) {
      skillNameCounts.set(f.skill, new Set());
    }
    skillNameCounts.get(f.skill)!.add(f.module);
  });
  skillNameCounts.forEach((modules, skill) => {
    if (modules.size > 1) {
      warnings.push(`INFO: Skill name "${skill}" appears in multiple modules: ${Array.from(modules).join(', ')}. This is OK with module-scoped architecture.`);
    }
  });
  return warnings;
}

/**
 * Get the largest skills by family count (module-scoped).
 */
export function getLargestSkills(limit: number = 10): Array<{
  moduleScopedKey: string;
  skill: string;
  familyCount: number;
  module: ModuleId;
}> {
  const skillSizes: Array<{
    moduleScopedKey: string;
    skill: string;
    familyCount: number;
    module: ModuleId;
  }> = [];
  FAMILIES_BY_MODULE_SKILL.forEach((families, moduleScopedKey) => {
    const module = families[0].module;
    const skill = families[0].skill;
    skillSizes.push({
      moduleScopedKey,
      skill,
      familyCount: families.length,
      module
    });
  });
  return skillSizes.sort((a, b) => b.familyCount - a.familyCount).slice(0, limit);
}

/**
 * Get statistics about the taxonomy.
 */
export function getTaxonomyStats() {
  const moduleStats = Array.from(FAMILIES_BY_MODULE.entries()).map(([moduleId, families]) => {
    const skills = new Set(families.map(f => f.skill));
    return {
      module: moduleId,
      totalFamilies: families.length,
      totalSkills: skills.size,
      skillBreakdown: Array.from(skills).map(skill => ({
        skill,
        moduleScopedKey: getModuleScopedSkillKey(moduleId, skill),
        familyCount: families.filter(f => f.skill === skill).length
      })).sort((a, b) => b.familyCount - a.familyCount)
    };
  });
  return {
    totalFamilies: TAXONOMY.length,
    totalModules: FAMILIES_BY_MODULE.size,
    totalModuleScopedSkills: FAMILIES_BY_MODULE_SKILL.size,
    moduleBreakdown: moduleStats,
    largestSkills: getLargestSkills(10),
    hierarchy: {
      description: 'Module → Skill → SubSkill → FamilyId',
      rules: {
        modules: '10 fixed modules (broad subject areas)',
        skills: '~47 module-scoped skills total (~5-10 per module, thematic groupings)',
        subSkills: '263 subSkills (1:1 with families, specific competencies)',
        familyIds: '263 unique identifiers (UPPERCASE-HYPHEN format)'
      },
      overlap: {
        skills: 'CAN overlap within a module - multiple families per skill',
        skillsAcrossModules: 'MODULE-SCOPED - same name in different modules = different skills',
        subSkills: 'NEVER overlap - strict 1:1 with familyId',
        familyIds: 'NEVER overlap - absolutely unique'
      },
      moduleScopedArchitecture: {
        enabled: true,
        format: 'ModuleId::skill (e.g., "Tijd::snelheid", "SpellingWW::leenwoorden")',
        benefit: 'Prevents skill name collisions across modules',
        example: 'SpellingWW::leenwoorden (3 families) ≠ SpellingRegel::leenwoorden (5 families)'
      }
    }
  };
}